import React from 'react';
import { Link } from 'react-router-dom';
import { useMarket } from '../../context/MarketContext';
import { getWebDevelopmentPricing } from '../../data/webDevelopmentPricing';
import { Seo } from '../../components/Seo';
import { keywordsServices } from '../../seo/keywords';
import { breadcrumbJsonLd } from '../../seo/site';

const WebDevelopment: React.FC = () => {
  const { market } = useMarket();
  const pricing = getWebDevelopmentPricing(market);

  return (
    <div className="pt-24">
      <Seo
        title="Web Development Services | Custom Websites & Web Apps"
        description="Custom website and web application development from Cimmple—design, build, and launch for growing businesses."
        keywords={['web development services', 'custom website development', 'web application development', ...keywordsServices]}
        jsonLd={breadcrumbJsonLd([
          { name: 'Home', path: '/' },
          { name: 'Services', path: '/services' },
          { name: 'Web Development', path: '/services/web-development' },
        ])}
      />
      {/* Hero Section */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="mb-8">
            <ol className="flex items-center gap-2 text-sm text-slate-500 dark:text-dark-400">
              <li><Link to="/" className="hover:text-slate-900 dark:hover:text-white transition-colors">Home</Link></li>
              <li>/</li>
              <li><Link to="/services" className="hover:text-slate-900 dark:hover:text-white transition-colors">Services</Link></li>
              <li>/</li>
              <li className="text-slate-900 dark:text-white font-medium">Web Development</li>
            </ol>
          </nav>

          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="w-24 h-24 rounded-3xl bg-primary-100 dark:bg-primary-500/10 text-primary-600 dark:text-primary-400 flex items-center justify-center mb-8">
                <svg className="w-16 h-16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
                </svg>
              </div>
              <h1 className="text-4xl md:text-5xl font-display font-bold text-slate-900 dark:text-white mb-4">
                Web Development
              </h1>
              <p className="text-xl text-primary-600 dark:text-primary-400 font-medium mb-6">
                Professional website solutions for machine shops & manufacturing businesses
              </p>
              <p className="text-slate-600 dark:text-dark-300 text-lg">
                When a potential customer searches for your company, your website can be the difference between getting an inquiry and losing an opportunity. We help machine shops, manufacturers, fabricators, and industrial businesses create professional websites that clearly communicate what they do, showcase their capabilities, and make it easy for customers to get in touch.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 mt-8">
                <Link to="/contact" className="btn-primary">
                  Get a Quote
                </Link>
                <Link to="/services" className="btn-secondary">
                  View All Services
                </Link>
              </div>
            </div>

            <div className="card p-8">
              <h3 className="text-xl font-display font-bold text-slate-900 dark:text-white mb-2">Complete website solutions</h3>
              <p className="text-sm text-primary-600 dark:text-primary-400 font-medium mb-6">
                Design · Development · Domain · Hosting · Support
              </p>
              <ul className="space-y-4">
                {[
                  'Showcase machining, fabrication, and manufacturing capabilities',
                  'Build credibility with customers, engineers, and purchasing teams',
                  'Make it easy to request information or submit an RFQ',
                  'Look professional on desktop, tablet, and smartphone',
                  'Stay available 24/7 so customers can learn about you anytime',
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <div className="w-6 h-6 rounded-full flex-shrink-0 flex items-center justify-center bg-primary-100 dark:bg-primary-500/20 text-primary-600 dark:text-primary-400">
                      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <span className="text-slate-600 dark:text-dark-300">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* What your website can do */}
      <section className="py-20 bg-slate-50 dark:bg-dark-900/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="section-title">
              What your website can do for{' '}
              <span className="gradient-text">your business</span>
            </h2>
            <p className="section-subtitle mx-auto mt-4">
              Turn your business into a stronger online presence that works for you around the clock.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                title: 'Showcase your capabilities',
                description: 'Present your machining, manufacturing, fabrication, equipment, facilities, materials, and technical capabilities in a professional format.',
                icon: (
                  <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                  </svg>
                ),
              },
              {
                title: 'Build customer confidence',
                description: 'A modern website helps establish credibility with potential customers, purchasing departments, engineers, and business partners.',
                icon: (
                  <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                  </svg>
                ),
              },
              {
                title: 'Generate more inquiries',
                description: 'Make it easy for visitors to contact you, request information, or submit an RFQ.',
                icon: (
                  <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
                  </svg>
                ),
              },
              {
                title: 'Look professional on every device',
                description: 'Your website will be designed to work across desktops, tablets, and smartphones.',
                icon: (
                  <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 18h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                  </svg>
                ),
              },
              {
                title: 'Be available 24/7',
                description: 'Customers can learn about your company, services, and capabilities even outside normal business hours.',
                icon: (
                  <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                ),
              },
              {
                title: 'Customized to your needs',
                description: 'Your website content and structure are tailored to your company rather than limited to a one-size-fits-all template.',
                icon: (
                  <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M11 4a2 2 0 114 0v1a1 1 0 001 1h3a1 1 0 011 1v3a1 1 0 01-1 1h-1a2 2 0 100 4h1a1 1 0 011 1v3a1 1 0 01-1 1h-3a1 1 0 01-1-1v-1a2 2 0 10-4 0v1a1 1 0 01-1 1H7a1 1 0 01-1-1v-3a1 1 0 00-1-1H4a2 2 0 110-4h1a1 1 0 001-1V7a1 1 0 011-1h3a1 1 0 001-1V4z" />
                  </svg>
                ),
              },
            ].map((item) => (
              <div key={item.title} className="card p-6">
                <div className="w-14 h-14 rounded-2xl bg-primary-100 dark:bg-primary-500/10 text-primary-600 dark:text-primary-400 flex items-center justify-center mb-5">
                  {item.icon}
                </div>
                <h3 className="text-lg font-display font-bold text-slate-900 dark:text-white mb-2">{item.title}</h3>
                <p className="text-slate-600 dark:text-dark-400 text-sm">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* What we can build */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="section-title">
              What we can <span className="gradient-text">build</span>
            </h2>
            <p className="section-subtitle mx-auto mt-4">
              Your website can include the pages and features that make sense for your business.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            <div className="card p-8">
              <h3 className="text-xl font-display font-bold text-slate-900 dark:text-white mb-6">Standard pages</h3>
              <ul className="grid sm:grid-cols-2 gap-3">
                {[
                  'Home',
                  'About Us',
                  'Products & Services',
                  'Capabilities',
                  'Industries Served',
                  'Equipment & Facilities',
                  'Projects / Portfolio',
                  'Gallery',
                  'Certifications',
                  'Contact Us',
                ].map((page) => (
                  <li key={page} className="flex items-center gap-2 text-sm text-slate-600 dark:text-dark-300">
                    <svg className="w-4 h-4 text-primary-600 dark:text-primary-500 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                    {page}
                  </li>
                ))}
              </ul>
            </div>

            <div className="card p-8">
              <h3 className="text-xl font-display font-bold text-slate-900 dark:text-white mb-6">Optional features</h3>
              <ul className="grid sm:grid-cols-2 gap-3">
                {[
                  'Request a Quote / RFQ form',
                  'Contact forms',
                  'Click-to-call',
                  'Email integration',
                  'Google Maps',
                  'WhatsApp integration',
                  'Social media links',
                  'Business email',
                  'Basic SEO setup',
                  'Additional product/service pages',
                ].map((feature) => (
                  <li key={feature} className="flex items-center gap-2 text-sm text-slate-600 dark:text-dark-300">
                    <svg className="w-4 h-4 text-primary-600 dark:text-primary-500 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                    {feature}
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <p className="text-center text-slate-600 dark:text-dark-400 mt-8 max-w-2xl mx-auto">
            Your website is customized according to your requirements—you don't have to pay for features you don't need.
          </p>
        </div>
      </section>

      {/* How it works */}
      <section className="py-20 bg-slate-50 dark:bg-dark-900/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="section-title">
              How it <span className="gradient-text">works</span>
            </h2>
            <p className="section-subtitle mx-auto mt-4">
              A clear process from first conversation to a live website—and ongoing support.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              { step: '01', title: 'Discuss', description: 'We learn about your business, services, capabilities, and website requirements.' },
              { step: '02', title: 'Plan', description: 'We recommend the appropriate website structure, pages, and features.' },
              { step: '03', title: 'Build', description: 'We design and develop your website according to the agreed requirements.' },
              { step: '04', title: 'Review', description: 'You review the website and provide feedback for the agreed revisions.' },
              { step: '05', title: 'Launch', description: 'We handle the domain, hosting, technical setup, and website deployment.' },
              { step: '06', title: 'Support', description: "We're available for ongoing technical support and additional updates as needed." },
            ].map((item) => (
              <div key={item.step} className="card text-center">
                <div className="text-4xl font-display font-bold text-primary-500/30 mb-4">{item.step}</div>
                <h3 className="text-lg font-display font-bold text-slate-900 dark:text-white mb-2">{item.title}</h3>
                <p className="text-slate-600 dark:text-dark-400 text-sm">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why work with us */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="section-title">
              Why work with <span className="gradient-text">us</span>
            </h2>
            <p className="section-subtitle mx-auto mt-4">
              Straightforward service built around what manufacturing businesses actually need.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {[
              {
                title: 'One point of contact',
                description: 'Website, domain, hosting, and technical support can all be handled through one provider.',
                icon: (
                  <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                ),
              },
              {
                title: 'Flexible solutions',
                description: "You don't have to pay for features you don't need. We build according to your actual requirements.",
                icon: (
                  <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM4 13a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H5a1 1 0 01-1-1v-6zM16 13a1 1 0 011-1h2a1 1 0 011 1v6a1 1 0 01-1 1h-2a1 1 0 01-1-1v-6z" />
                  </svg>
                ),
              },
              {
                title: 'Professional design',
                description: 'We focus on creating a clean, modern website that represents your business professionally.',
                icon: (
                  <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01" />
                  </svg>
                ),
              },
              {
                title: 'Straightforward pricing',
                description: "Website development and recurring services are clearly separated, so you know what you're paying for.",
                icon: (
                  <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                  </svg>
                ),
              },
            ].map((item) => (
              <div key={item.title} className="card flex items-center gap-6 p-6">
                <div className="w-14 h-14 rounded-2xl bg-primary-100 dark:bg-primary-500/10 text-primary-600 dark:text-primary-400 flex items-center justify-center flex-shrink-0">
                  {item.icon}
                </div>
                <div>
                  <h3 className="text-xl font-display font-bold text-slate-900 dark:text-white">{item.title}</h3>
                  <p className="text-slate-600 dark:text-dark-400 text-sm">{item.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Packages */}
      <section className="py-20 bg-slate-50 dark:bg-dark-900/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="section-title">
              Simple, transparent <span className="gradient-text">pricing</span>
            </h2>
            <p className="section-subtitle mx-auto mt-4">
              Choose the package that fits your business—or tell us what you need and we'll recommend one.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 items-stretch">
            {pricing.plans.map((plan) => (
              <div
                key={plan.name}
                className={`relative rounded-2xl p-8 flex flex-col transition-all duration-300 ${
                  plan.highlighted
                    ? 'bg-gradient-to-b from-primary-100 dark:from-primary-500/10 to-white dark:to-dark-900/50 border-2 border-primary-500/50 shadow-xl shadow-primary-500/10 md:scale-105'
                    : 'card hover:border-slate-300 dark:hover:border-dark-600'
                }`}
              >
                {plan.badge && (
                  <span className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full bg-primary-600 text-white text-xs font-medium whitespace-nowrap">
                    {plan.badge}
                  </span>
                )}
                <div className="text-center mb-6">
                  <h3 className="text-xl font-display font-bold text-slate-900 dark:text-white mb-2">
                    {plan.name}
                  </h3>
                  <div className="mb-3">
                    <span className="text-4xl font-display font-bold text-slate-900 dark:text-white">
                      {plan.price}
                    </span>
                    {plan.period && (
                      <span className="text-slate-500 dark:text-dark-400 text-sm ml-2">{plan.period}</span>
                    )}
                  </div>
                  <p className="text-slate-500 dark:text-dark-400 text-sm">
                    {plan.description}
                  </p>
                </div>

                <p className="text-xs uppercase tracking-wide font-semibold text-slate-500 dark:text-dark-400 mb-3">
                  {plan.includesLabel}
                </p>
                <ul className="space-y-3 mb-6 flex-1">
                  {plan.features.map((feature) => (
                    <li key={feature} className="flex items-start gap-3 text-sm text-slate-700 dark:text-dark-300">
                      <svg
                        className={`w-5 h-5 flex-shrink-0 mt-0.5 ${
                          plan.highlighted
                            ? 'text-primary-600 dark:text-primary-400'
                            : 'text-green-600 dark:text-green-500'
                        }`}
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>

                {plan.idealFor && (
                  <p className="text-sm text-slate-600 dark:text-dark-400 mb-6">
                    <span className="font-semibold text-slate-900 dark:text-white">{plan.idealForLabel}:</span> {plan.idealFor}
                  </p>
                )}
                {plan.note && (
                  <p className="text-sm text-slate-600 dark:text-dark-400 mb-6">{plan.note}</p>
                )}

                <Link
                  to="/contact"
                  className={`block text-center w-full mt-auto ${plan.highlighted ? 'btn-primary' : 'btn-secondary'}`}
                >
                  Get a Quote
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Domain & Hosting */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="section-title">
              Domain & <span className="gradient-text">hosting</span>
            </h2>
            <p className="section-subtitle mx-auto mt-4">
              {pricing.domainHostingDisclaimer}
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            <div className="card p-6">
              <h3 className="text-lg font-display font-bold text-slate-900 dark:text-white mb-3 text-center">
                {pricing.domain.title}
              </h3>
              <p className="mb-4 text-center text-slate-900 dark:text-white">
                <span className="text-3xl font-display font-bold">{pricing.domain.price}</span>
                <span className="text-slate-500 dark:text-dark-400 text-sm ml-1">{pricing.domain.period}</span>
              </p>
              <p className="text-slate-600 dark:text-dark-400 text-sm mb-3">
                {pricing.domain.description}
              </p>
              {pricing.domain.note && (
                <p className="text-xs text-slate-500 dark:text-dark-500 italic">
                  {pricing.domain.note}
                </p>
              )}
            </div>

            <div className="card p-6">
              <h3 className="text-lg font-display font-bold text-slate-900 dark:text-white mb-3 text-center">
                {pricing.hosting.title}
              </h3>
              <p className="mb-4 text-center text-slate-900 dark:text-white">
                <span className="text-3xl font-display font-bold">{pricing.hosting.price}</span>
                <span className="text-slate-500 dark:text-dark-400 text-sm ml-1">{pricing.hosting.period}</span>
              </p>
              {pricing.hosting.includes && (
                <ul className="space-y-2">
                  {pricing.hosting.includes.map((item) => (
                    <li key={item} className="flex items-center gap-2 text-sm text-slate-600 dark:text-dark-400">
                      <svg className="w-4 h-4 text-primary-600 dark:text-primary-500 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                      {item}
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div className="card p-6">
              <h3 className="text-lg font-display font-bold text-slate-900 dark:text-white mb-3 text-center">
                {pricing.maintenance.title}
              </h3>
              <p className="mb-4 text-center text-slate-900 dark:text-white">
                <span className="text-3xl font-display font-bold">{pricing.maintenance.price}</span>
                <span className="text-slate-500 dark:text-dark-400 text-sm ml-1">{pricing.maintenance.period}</span>
              </p>
              <p className="text-slate-600 dark:text-dark-400 text-sm">
                {pricing.maintenance.description}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Optional Services */}
      <section className="py-20 bg-slate-50 dark:bg-dark-900/30">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="section-title">
              Optional <span className="gradient-text">services</span>
            </h2>
            <p className="section-subtitle mx-auto mt-4">
              Add-ons you can include as needed.
            </p>
          </div>

          <div className="card overflow-hidden">
            <div className="divide-y divide-slate-200 dark:divide-dark-700">
              {pricing.optionalServices.map((row) => (
                <div
                  key={row.service}
                  className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1 px-6 py-4"
                >
                  <span className="text-slate-700 dark:text-dark-300">{row.service}</span>
                  <span className="font-semibold text-slate-900 dark:text-white sm:text-right">{row.price}</span>
                </div>
              ))}
            </div>
          </div>
          <p className="text-sm text-slate-500 dark:text-dark-400 text-center mt-6 max-w-2xl mx-auto">
            {pricing.optionalDisclaimer}
          </p>
        </div>
      </section>

      {/* Get Started CTA */}
      <section className="py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="card p-12 relative overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-br from-primary-500/10 to-accent-500/10" />
            <div className="relative text-center">
              <h2 className="text-3xl md:text-4xl font-display font-bold text-slate-900 dark:text-white mb-4">
                {pricing.cta.title}
              </h2>
              <p className="text-slate-700 dark:text-dark-300 text-lg mb-8 max-w-2xl mx-auto">
                {pricing.cta.description}
              </p>

              <div className="card bg-white/80 dark:bg-dark-900/60 p-6 max-w-md mx-auto mb-8 text-left">
                <p className="text-sm font-semibold uppercase tracking-wide text-primary-600 dark:text-primary-400 mb-3">
                  {pricing.cta.bundleLabel}
                </p>
                <p className="text-2xl font-display font-bold text-slate-900 dark:text-white mb-4">
                  {pricing.cta.totalLabel}: {pricing.cta.total}
                </p>
                <ul className="space-y-2 text-sm text-slate-600 dark:text-dark-300 mb-4">
                  {pricing.cta.lineItems.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
                <p className="text-xs text-slate-500 dark:text-dark-400">
                  {pricing.cta.footerNote}
                </p>
              </div>

              <p className="text-slate-600 dark:text-dark-400 text-sm mb-8">
                {pricing.cta.quoteNote}
              </p>
              <Link to="/contact" className="btn-primary">
                Get a Quote
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default WebDevelopment;
